import { useCallback, useEffect, useState } from "react";
import {
  Activity, AlertTriangle, Building2, CheckCircle2, ChevronLeft,
  ChevronRight, CircleOff, ExternalLink, Globe2, Loader2, RefreshCw,
  Search, Server, ShieldCheck, Users, XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";


const STATUS_OPTIONS = ["", "active", "suspended", "disabled", "deleted"];

function readable(value) {
  return String(value || "unknown").replaceAll("_", " ");
}

function when(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function StatusPill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
      ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
    }`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value ?? "-"}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <h2 className="mb-4 flex items-center gap-2 font-semibold text-slate-900">
        <Icon className="h-4 w-4 text-emerald-700" /> {title}
      </h2>
      {children}
    </section>
  );
}

function DataRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-100 py-2.5 last:border-0 sm:flex-row sm:justify-between">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="break-all text-sm font-medium text-slate-800 sm:max-w-[65%] sm:text-right">{children ?? "-"}</dd>
    </div>
  );
}

export default function AdminOperations() {
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], pagination: { page: 1, pages: 0, total: 0 } });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const loadList = useCallback(async () => {
    setListLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/operations/companies", {
        params: { q: query || undefined, status: status || undefined, page, limit: 25 },
      });
      setList(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setListLoading(false);
    }
  }, [page, query, status]);

  useEffect(() => { loadList(); }, [loadList]);

  const selectCompany = async (companyId) => {
    setSelectedId(companyId);
    setDetail(null);
    setDetailLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/operations/companies/${encodeURIComponent(companyId)}`);
      setDetail(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(queryInput.trim());
  };

  return (
    <div className="space-y-5" data-testid="admin-operations">
      <header>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-emerald-700" />
          <h1 className="text-2xl font-semibold text-slate-900">Support Operations</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Read-only business diagnostics. No account or business data can be changed here.
        </p>
      </header>

      <form onSubmit={submitSearch} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[1fr_180px_auto]">
        <label>
          <span className="sr-only">Company name, slug, or ID</span>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input value={queryInput} onChange={(event) => setQueryInput(event.target.value)}
                   maxLength={120} placeholder="Company name, slug, or company ID"
                   className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100" />
          </div>
        </label>
        <label>
          <span className="sr-only">Account status</span>
          <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}
                  className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-emerald-600">
            {STATUS_OPTIONS.map((item) => <option key={item || "all"} value={item}>{item ? readable(item) : "All statuses"}</option>)}
          </select>
        </label>
        <button type="submit" className="h-10 rounded-lg bg-emerald-700 px-5 text-sm font-semibold text-white hover:bg-emerald-800">
          Search
        </button>
      </form>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      <div className="grid min-h-[500px] gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Companies ({list.pagination.total || 0})</p>
            <button type="button" onClick={loadList} aria-label="Refresh companies"
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-100">
              <RefreshCw className={`h-4 w-4 ${listLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {listLoading ? (
            <div className="flex justify-center p-10"><Loader2 className="h-5 w-5 animate-spin text-emerald-700" /></div>
          ) : list.items.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500">No companies match this search.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.items.map((item) => (
                <li key={item.company_id}>
                  <button type="button" onClick={() => selectCompany(item.company_id)}
                          className={`w-full p-4 text-left hover:bg-slate-50 ${selectedId === item.company_id ? "bg-emerald-50" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-slate-900">{item.name}</span>
                      <StatusPill ok={item.account_enabled}>{readable(item.account_status)}</StatusPill>
                    </div>
                    <p className="mt-1 truncate font-mono text-xs text-slate-500">{item.company_id}</p>
                    <p className="mt-2 text-xs text-slate-600">{readable(item.plan)} · {readable(item.subscription_status)}</p>
                    {item.health_indicators.length > 0 && (
                      <p className="mt-2 text-xs font-medium text-amber-700">{item.health_indicators.length} item(s) need attention</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-slate-600">
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}
                    className="rounded p-1.5 hover:bg-slate-100 disabled:opacity-30" aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </button>
            Page {list.pagination.page || page} of {Math.max(list.pagination.pages || 0, 1)}
            <button type="button" disabled={page >= (list.pagination.pages || 1)} onClick={() => setPage((value) => value + 1)}
                    className="rounded p-1.5 hover:bg-slate-100 disabled:opacity-30" aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </aside>

        <main className="min-w-0">
          {!selectedId ? (
            <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <Building2 className="h-9 w-9 text-slate-300" />
              <p className="mt-3 font-medium text-slate-700">Select a company to view diagnostics</p>
              <p className="mt-1 text-sm text-slate-500">Diagnostics are fetched only for the selected company.</p>
            </div>
          ) : detailLoading ? (
            <div className="flex min-h-[400px] items-center justify-center rounded-xl border border-slate-200 bg-white">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />
            </div>
          ) : detail && <Diagnostics detail={detail} onRefresh={() => selectCompany(selectedId)} />}
        </main>
      </div>
    </div>
  );
}

function Diagnostics({ detail, onRefresh }) {
  const { company, owner, access, counts, warnings, sessions, integrations, activity, verification, request_errors: requestErrors } = detail;
  const website = integrations.website;
  return (
    <div className="space-y-4" data-testid="support-diagnostics">
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-slate-900">{company.name}</h2>
            <StatusPill ok={company.account_enabled}>{readable(company.account_status)}</StatusPill>
          </div>
          <p className="mt-1 font-mono text-xs text-slate-500">{company.company_id}</p>
          <p className="mt-2 text-sm text-slate-600">Last meaningful activity: {when(activity.latest_meaningful_at)}</p>
        </div>
        <button type="button" onClick={onRefresh} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 text-sm hover:bg-slate-50">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Vehicles" value={counts.vehicles} hint="vehicles collection" />
        <Metric label="Bookings" value={counts.bookings} />
        <Metric label="Open support tickets" value={counts.open_support_tickets} />
        <Metric label="Active warnings" value={warnings.active_total} hint="Stored records only" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Account and owner" icon={ShieldCheck}>
          <dl>
            <DataRow label="Plan">{readable(company.plan)}</DataRow>
            <DataRow label="Subscription">{readable(company.subscription_status)}</DataRow>
            <DataRow label="Trial ends">{when(company.trial_ends_at)}</DataRow>
            <DataRow label="Owner user ID">{owner.user_id || "Missing"}</DataRow>
            <DataRow label="Owner record"><StatusPill ok={owner.record_found}>{owner.record_found ? "Found" : "Missing"}</StatusPill></DataRow>
            <DataRow label="Owner account"><StatusPill ok={owner.account_enabled}>{owner.account_enabled ? "Enabled" : "Disabled"}</StatusPill></DataRow>
            <DataRow label="Owner email"><StatusPill ok={owner.email_verified}>{owner.email_verified ? "Verified" : "Unverified"}</StatusPill></DataRow>
          </dl>
        </Section>

        <Section title="Business access" icon={Users}>
          <dl>
            <DataRow label="Legacy active staff">{access.legacy_active_staff}</DataRow>
            <DataRow label="Active memberships">{access.active_business_memberships}</DataRow>
            <DataRow label="Membership runtime">{readable(access.membership_runtime_state)}</DataRow>
            <DataRow label="Active owner sessions">{sessions.active_count}</DataRow>
            <DataRow label="Latest owner session activity">{when(sessions.latest_activity_at)}</DataRow>
            <DataRow label="Customer-history access">{readable(verification.customer_history_policy)}</DataRow>
            <DataRow label="Customer aggregate">Not available until verified booking linking runtime</DataRow>
          </dl>
        </Section>

        <Section title="Website and integration shell" icon={Globe2}>
          <dl>
            <DataRow label="Connected"><StatusPill ok={website.connected}>{website.connected ? "Connected" : "Not connected"}</StatusPill></DataRow>
            <DataRow label="Hostname">{website.hostname || "Not available"}</DataRow>
            <DataRow label="Ownership verification">{readable(website.verification_status)}</DataRow>
            <DataRow label="Verified at">{when(website.verified_at)}</DataRow>
            <DataRow label="Admin disabled">{website.admin_disabled ? "Yes" : "No"}</DataRow>
            <DataRow label="Hosted page shell">{website.hosted_shell_enabled ? "Enabled" : "Disabled"}</DataRow>
            <DataRow label="Widget shell">{website.widget_shell_enabled ? "Enabled" : "Disabled"}</DataRow>
            <DataRow label="Booking submission">Not enabled: shell only</DataRow>
          </dl>
        </Section>

        <Section title="Warning summary" icon={AlertTriangle}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.entries(warnings.by_severity).map(([severity, count]) => (
              <div key={severity} className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs capitalize text-slate-500">{severity}</p>
                <p className="text-xl font-semibold text-slate-900">{count}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">Read directly from stored warning records. No warning scan was triggered.</p>
        </Section>
      </div>

      <Section title="Integration delivery outcomes" icon={Server}>
        {integrations.recent_webhook_outcomes.length === 0 ? (
          <p className="text-sm text-slate-500">No webhook delivery outcomes are available.</p>
        ) : (
          <div className="overflow-x-auto" role="region" aria-label="Integration delivery outcomes table" tabIndex={0}>
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase text-slate-500"><tr><th className="py-2">Event</th><th>Outcome</th><th>Status</th><th>Attempts</th><th>Time</th></tr></thead>
              <tbody>{integrations.recent_webhook_outcomes.map((row) => (
                <tr key={row.delivery_id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 font-medium">{row.event || "Unknown event"}</td><td>{readable(row.outcome)}</td><td>{row.status_code ?? "-"}</td><td>{row.attempts ?? "-"}</td><td>{when(row.settled_at || row.created_at)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Recent meaningful audit activity" icon={Activity}>
        {activity.recent.length === 0 ? (
          <p className="text-sm text-slate-500">No audit activity is available.</p>
        ) : (
          <ol className="space-y-2">
            {activity.recent.map((row) => (
              <li key={row.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between"><span className="font-medium text-slate-800">{readable(row.action_type)}</span><time className="text-xs text-slate-500">{when(row.created_at)}</time></div>
                {row.user_id && <p className="mt-1 font-mono text-xs text-slate-500">Actor: {row.user_id}</p>}
              </li>
            ))}
          </ol>
        )}
        <Link to="/admin/audit-logs" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline">
          Open Audit Logs <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </Section>

      <section className="rounded-xl border border-slate-200 bg-slate-100 p-4 text-sm text-slate-700">
        <div className="flex items-start gap-2">
          {requestErrors.connected ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" /> : <CircleOff className="mt-0.5 h-4 w-4 text-slate-500" />}
          <div><p className="font-medium">Request error observability</p><p className="mt-1">{requestErrors.message}</p></div>
        </div>
      </section>
    </div>
  );
}
